import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  MenuItem,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import InputBase from "@mui/material/InputBase";
import { alpha, styled } from "@mui/material/styles";

const steps = ["Profile", "Products", "KYC"];

const countries = [
  { value: "US", label: "United States" },
  { value: "UK", label: "United Kingdom" },
];

const productShellOptions = ["Basmati Rice", "Amaranth"];
const productBuyOptions = ["Barley", "Buckwheat"];

const BootstrapInput = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(3),
  },
  "& .MuiInputBase-input": {
    borderRadius: 4,
    position: "relative",
    backgroundColor: "#F3F6F9",
    border: "1px solid",
    borderColor: "#E0E3E7",
    fontSize: 16,
    width: "100%",
    padding: "10px 12px",
    transition: theme.transitions.create([
      "border-color",
      "background-color",
      "box-shadow",
    ]),
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
    "&:focus": {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
      borderColor: theme.palette.primary.main,
    },
  },
}));

// 🔹 Custom InputLabel without margin/padding
const StyledInputLabel = styled(InputLabel)(() => ({
  margin: "2px",
  padding: 0,
  transform: "none",

  fontSize: "14px",
  lineHeight: "1.5",
  color: "#444",
}));

export default function ProfilekycStepper() {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    fullName: "",
    address: "",
    country: "US",
    phoneNumber: "",
    productsToSell: [],
    productsToBuy: [],
    documentName: "",
    documentIdNumber: "",
    uploadIdPhoto: null,
  });

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      uploadIdPhoto: file || null,
    }));
  };

  const handleProductsChange = (name) => (event, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fileInputStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FF9800",
    color: "#fff",
    borderRadius: "4px",
    height: "40px",
    border: "none",
    width: "100%",
    "&:hover": {
      backgroundColor: "#fb8c00",
      cursor: "pointer",
    },
    "& input": {
      display: "none",
    },
    "& label": {
      color: "#fff",
      fontSize: "1rem",
      cursor: "pointer",
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  return (
    <div className="kyc-page">
      <div className="section-cs">
        <Box sx={{ width: "100%", padding: 2 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepLabel-label.Mui-active": { color: "#FF9800" },
              "& .MuiStepLabel-label.Mui-completed": { color: "#FF9800" },
              "& .MuiStepIcon-root.Mui-active": { color: "#FF9800" },
              "& .MuiStepIcon-root.Mui-completed": { color: "#FF9800" },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1 */}
          {activeStep === 0 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minHeight: 300,
              }}
            >
              <h2>Profile Information</h2>
              <FormControl fullWidth>
                <StyledInputLabel shrink htmlFor="fullName-input">
                  Full Name
                </StyledInputLabel>
                <BootstrapInput
                  id="fullName-input"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="e.g., John Doe"
                />
              </FormControl>

              <FormControl fullWidth>
                <StyledInputLabel shrink htmlFor="address-input">
                  Address
                </StyledInputLabel>
                <BootstrapInput
                  id="address-input"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="e.g., 123 Main St, City, Country"
                />
              </FormControl>

              <FormControl fullWidth>
                <StyledInputLabel shrink>Country</StyledInputLabel>
                <Select
                  value={formData.country}
                  name="country"
                  onChange={handleChange}
                  input={<BootstrapInput />}
                >
                  {countries.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <StyledInputLabel shrink htmlFor="phoneNumber-input">
                  Phone Number
                </StyledInputLabel>
                <BootstrapInput
                  id="phoneNumber-input"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="e.g., +1 123-456-7890"
                />
              </FormControl>

              <div className="section-cs1">
                <Button
                  className="submit-btn-more"
                  onClick={handleNext}
                  style={{ backgroundColor: "#FF9800", color: "#fff" }}
                >
                  Next
                </Button>
              </div>
            </Box>
          )}

          {/* Step 2 */}
          {activeStep === 1 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minHeight: 300,
              }}
            >
              <h2>Products</h2>
              <FormControl fullWidth variant="standard" sx={{ mb: 3 }}>
                <StyledInputLabel shrink>Products to Sell</StyledInputLabel>
                <Autocomplete
                  multiple
                  fullWidth
                  options={productShellOptions}
                  value={formData.productsToSell}
                  onChange={handleProductsChange("productsToSell")}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <BootstrapInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      placeholder="Select products to sell"
                      fullWidth
                    />
                  )}
                />
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {formData.productsToSell.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      onDelete={() =>
                        handleProductsChange("productsToSell")(
                          null,
                          formData.productsToSell.filter((p) => p !== item)
                        )
                      }
                    />
                  ))}
                </Box>
              </FormControl>

              <FormControl fullWidth variant="standard">
                <StyledInputLabel shrink>Products to Buy</StyledInputLabel>
                <Autocomplete
                  multiple
                  fullWidth
                  options={productBuyOptions}
                  value={formData.productsToBuy}
                  onChange={handleProductsChange("productsToBuy")}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <BootstrapInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      placeholder="Select products to buy"
                      fullWidth
                    />
                  )}
                />
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {formData.productsToBuy.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      onDelete={() =>
                        handleProductsChange("productsToBuy")(
                          null,
                          formData.productsToBuy.filter((p) => p !== item)
                        )
                      }
                    />
                  ))}
                </Box>
              </FormControl>

              <div
                className="section-cs1"
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Button
                  className="submit-btn-more"
                  onClick={handleBack}
                  style={{ backgroundColor: "#FF9800", color: "#fff" }}
                >
                  Back
                </Button>
                <Button
                  className="submit-btn-more"
                  onClick={handleNext}
                  style={{ backgroundColor: "#FF9800", color: "#fff" }}
                >
                  Next
                </Button>
              </div>
            </Box>
          )}

          {/* Step 3 */}
          {activeStep === 2 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minHeight: 300,
              }}
            >
              <h2>KYC</h2>
              <FormControl fullWidth>
                <StyledInputLabel shrink htmlFor="documentName-input">
                  Document Name
                </StyledInputLabel>
                <BootstrapInput
                  id="documentName-input"
                  name="documentName"
                  value={formData.documentName}
                  onChange={handleChange}
                  placeholder="e.g., Passport"
                />
              </FormControl>

              <FormControl fullWidth>
                <StyledInputLabel shrink htmlFor="documentIdNumber-input">
                  Document ID Number
                </StyledInputLabel>
                <BootstrapInput
                  id="documentIdNumber-input"
                  name="documentIdNumber"
                  value={formData.documentIdNumber}
                  onChange={handleChange}
                  placeholder="e.g., A12345678"
                />
              </FormControl>

              <Box sx={fileInputStyles}>
                <label htmlFor="upload-id-photo">
                  {formData.uploadIdPhoto
                    ? formData.uploadIdPhoto.name
                    : "Upload ID Photo"}
                </label>
                <input
                  id="upload-id-photo"
                  type="file"
                  onChange={handleFileChange}
                />
              </Box>

              {formData.uploadIdPhoto && (
                <Box sx={{ mt: 1, color: "#555", fontSize: "14px" }}>
                  <span>Selected file: {formData.uploadIdPhoto.name}</span>
                </Box>
              )}

              <div
                className="section-cs1"
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Button
                  className="submit-btn-more"
                  onClick={handleBack}
                  style={{ backgroundColor: "#FF9800", color: "#fff" }}
                >
                  Back
                </Button>
                <Button
                  className="submit-btn-more"
                  onClick={handleNext}
                  style={{ backgroundColor: "#FF9800", color: "#fff" }}
                >
                  Submit
                </Button>
              </div>
            </Box>
          )}
        </Box>
      </div>
    </div>
  );
}
